import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { Device, PairedRuuviTag, RuuviTag } from '../../types'
import { getPairedRuuviTags, invokeAction } from '../../services/deviceService'
import { Alert, AlertColor, AlertTitle, Box, Button, Stack } from '@mui/material'
import { BluetoothSearching } from '@mui/icons-material'
import RuuviTagQrCodeButton from './RuuviTagQrCodeButton'
import LoadingState from '../LoadingState'
import RuuviTagMacAddressInput from './RuuviTagMacAddressInput'
import RuuviTagScannerList from './RuuviTagScannerList'
import ValveInstallRuuviTagInstructions from '../ValveInstallRuuviTagInstructions'
import { RuuviTagType } from '../../enums'
import {
  getRuuviTag,
  getRuuviTagAccelerationNow,
  getRuuviTagConditionNow
} from '../../services/ruuviTagService'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createInfoNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import { LoadingButton } from '@mui/lab'
import RuuviTagInstallationInfoAlert from '../RuuviTagInstallationInfoAlert'
import { usePolicy } from '../../hooks'
import { isValve } from '../../utils/device'
import { formatMacAddress } from '../../utils/string'
import DeviceDetachRuuviTagAlert from '../DeviceDetachRuuviTagAlert'
import RuuviTagCreateButton from './RuuviTagCreateButton'

const MAC_ADDRESS_LENGTH = 12

export interface RuuviTagScannerProps {
  /**
   * The tag type to be installed.
   */
  type?: RuuviTagType

  /**
   * The host device.
   */
  device: Device

  /**
   * Indicates if scanning of tags is allowed.
   */
  allowScan?: boolean

  onChange: (ruuviTag: RuuviTag | null) => void

  onDeviceChange?: (device: Device) => void

  onCancel: () => void
}

export default function RuuviTagScanner({
  type = RuuviTagType.DEVICE,
  device,
  allowScan = true,
  onChange,
  onDeviceChange,
  onCancel
}: RuuviTagScannerProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()

  const { canCreateRuuviTag } = usePolicy()

  const abortControllerRef = useRef<AbortController | null>(null)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const macSourceRef = useRef<'scan' | 'manual' | null>(null)

  const isMountedRef = useRef(true)

  /**
   * The tags found by scanning.
   */
  const [scannedRuuviTags, setScannedRuuviTags] = useState<PairedRuuviTag[]>([])

  /**
   * Indicates if the scanning is in progress.
   */
  const [isScanning, setScanning] = useState<boolean>(false)

  /**
   * Indicates if the scan has been completed.
   */
  const [isScanCompleted, setScanCompleted] = useState<boolean>(false)

  /**
   * Indicates if the loading is in progress.
   */
  const [isLoading, setLoading] = useState<boolean>(false)

  /**
   * The entered MAC address.
   */
  const [macAddress, setMacAddress] = useState<string>('')

  /**
   * The not found message.
   */
  const [notFoundMessage, setNotFoundMessage] = useState<string | null>(null)

  const [notFoundAlertSeverity, setNotFoundAlertSeverity] = useState<AlertColor>('error')

  const [isRefreshingOrientation, setRefreshingOrientation] = useState<boolean>(false)

  // FIXME: Not sure why this would be needed.
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Find the tag when the MAC address changes.
  useEffect(() => {
    if (macAddress.length !== MAC_ADDRESS_LENGTH) {
      setNotFoundMessage(null)
      setLoading(false)
      return
    }

    const controller = new AbortController()

    findRuuviTag(macAddress, controller)

    return () => controller.abort()
  }, [macAddress])

  // When scanning flag turns on, start the scan loop; clean up on stop/unmount
  useEffect(() => {
    if (!isScanning) {
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      scanRuuviTags(controller)
    } catch {
      // We can ignore the error for now.
    }

    return () => {
      controller.abort()

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isScanning])

  // Stop scanning when scanning is denied.
  useEffect(() => {
    if (!allowScan) {
      setScanning(false)
    }
  }, [allowScan])

  async function findRuuviTag(mac: string, controller?: AbortController) {
    try {
      setLoading(true)

      onChange(null)
      setNotFoundMessage(null)
      setNotFoundAlertSeverity('error')

      const tag = await getRuuviTag(mac, { signal: controller?.signal })

      dispatch(createSuccessNotification(t('foundRuuviTag')))

      onChange(tag)
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      } else if (axios.isAxiosError(error) && error.response?.status === 404) {
        const source = macSourceRef.current

        if (canCreateRuuviTag()) {
          setNotFoundAlertSeverity('info')
          setNotFoundMessage(t('ruuviTagCreatePromptMessage', { mac: formatMacAddress(mac) }))
        } else if (source === 'scan') {
          setNotFoundAlertSeverity('info')
          setNotFoundMessage(t('ruuviTagPowerOnReminder'))
          dispatch(createInfoNotification(t('ruuviTagPowerOnReminder')))
        } else {
          setNotFoundAlertSeverity('error')
          setNotFoundMessage(t('ruuviTagWasNotFoundByMacAddress'))
        }
      } else {
        setNotFoundMessage(null)
        dispatch(createErrorNotification(t('somethingWentWrong')))
      }
    } finally {
      setLoading(false)
    }
  }

  async function scanRuuviTags(controller?: AbortController) {
    if (!allowScan) {
      return
    }

    setScanCompleted(false)

    if (!isScanning) {
      setScanning(true)
    }

    setScannedRuuviTags([])

    // Invoke action for the device.
    await invokeAction(
      device.name,
      {
        ruuvi_pairing: 60
      },
      {
        signal: controller?.signal
      }
    )

    // Poll for the paired ruuvi tags every 2 seconds.
    const TWO_SECONDS = 2 * 1000
    const interval = setInterval(async () => {
      try {
        const tags = await getPairedRuuviTags(device.name, { signal: controller?.signal })

        setScannedRuuviTags((prev) => {
          if (tags.length === 0) {
            return prev
          }

          const tagsById = new Map(prev.map((tag) => [tag.id, tag]))
          tags.forEach((tag) => {
            tagsById.set(tag.id, tag)
          })

          return Array.from(tagsById.values())
        })
      } catch {
        // Do nothing if failed.
      }
    }, TWO_SECONDS)

    intervalRef.current = interval

    // Clear the interval after one minute.
    const ONE_MINUTE = 60 * 1000
    const timeout = setTimeout(() => {
      setScanning(false)
      setScanCompleted(true)
      clearInterval(interval)
    }, ONE_MINUTE)

    timeoutRef.current = timeout
  }

  async function handleSelect(tag: PairedRuuviTag) {
    onChange(tag)
  }

  function handleTagUpdate(tag: PairedRuuviTag) {
    setScannedRuuviTags((prev) => {
      const index = prev.findIndex((existing) => existing.id === tag.id)

      if (index === -1) {
        return [...prev, tag]
      }

      const next = [...prev]
      next[index] = {
        ...next[index],
        ...tag,
        latest: tag.latest ?? next[index].latest
      }

      return next
    })
  }

  function handleManualMacChange(value: string) {
    macSourceRef.current = 'manual'

    setMacAddress(value)
  }

  function handleQrMacDetected(value: string) {
    macSourceRef.current = 'scan'

    setMacAddress(value)
  }

  function startScan() {
    // Do not start scan when denied or already scanning.
    if (!allowScan || isScanning) {
      return
    }

    setScanCompleted(false)
    setScanning(true)
  }

  async function handleRefreshOrientation() {
    // Check if we are already refreshing orientation or scanning.
    if (isRefreshingOrientation || isScanning) {
      return
    }

    try {
      setRefreshingOrientation(true)

      const refreshedRuuviTags: PairedRuuviTag[] = []

      for (const tag of scannedRuuviTags) {
        try {
          const [condition, acceleration] = await Promise.all([
            getRuuviTagConditionNow(tag.id),
            getRuuviTagAccelerationNow(tag.id)
          ])

          refreshedRuuviTags.push({
            ...tag,
            latest: {
              ...tag.latest,
              condition,
              acceleration
            }
          })
        } catch {
          refreshedRuuviTags.push(tag)
        }
      }

      setScannedRuuviTags(refreshedRuuviTags)
    } catch {
      // We can ignore the error.
    } finally {
      setRefreshingOrientation(false)
    }
  }

  function handleCancelScan() {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort()
      } catch {
        /* ignore abort errors */
      }
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    setScanning(false)
    setScanCompleted(false)
  }

  function handleCancelCreate() {
    setMacAddress('')
  }

  async function handleAfterDetach(device: Device) {
    onDeviceChange?.(device)
  }

  // Check if attempting the install a VALVE tag to a non-valve device.
  if ((type === RuuviTagType.VALVE_IN || type === RuuviTagType.VALVE_OUT) && !isValve(device)) {
    return (
      <Alert severity="warning">
        <AlertTitle sx={{ m: 0 }}>{t('manufacturing:typeIsNotValve')}</AlertTitle>
      </Alert>
    )
  }

  // Check if the device is already associated with "fixed host" tag.
  // If so, then that tag needs to be detached before proceeding with the installation.
  if (
    (type === RuuviTagType.ATTACHED && device.attachedRuuviTagId !== null) ||
    (type === RuuviTagType.VALVE_IN && device.inRuuviTagId !== null) ||
    (type === RuuviTagType.VALVE_OUT && device.outRuuviTagId !== null)
  ) {
    return (
      <DeviceDetachRuuviTagAlert
        type={type}
        device={device}
        onCancel={onCancel}
        onUpdate={handleAfterDetach}
      />
    )
  }

  return (
    <>
      <Stack direction="column" gap={2}>
        {isValve(device) ? (
          <ValveInstallRuuviTagInstructions valve={device} />
        ) : (
          <RuuviTagInstallationInfoAlert />
        )}

        {!allowScan ? (
          <Alert severity="info">
            <AlertTitle sx={{ m: 0 }}>
              {t('ruuviTagDiscoveryDisabledWhileDeviceOffline')}
            </AlertTitle>
          </Alert>
        ) : (
          <>
            {isScanning ? (
              <LoadingState
                title={
                  <Stack direction="row" gap={1} alignItems="center">
                    <BluetoothSearching fontSize="small" />
                    <span>{t('searchingRuuviTags')}</span>
                  </Stack>
                }
                action={
                  <Button variant="outlined" color="inherit" onClick={handleCancelScan}>
                    {t('cancel')}
                  </Button>
                }
              />
            ) : isScanCompleted ? (
              <>
                {scannedRuuviTags.length === 0 ? (
                  <Alert severity="warning">
                    <AlertTitle sx={{ m: 0 }}>{t('ruuviTagsWereNotFound')}</AlertTitle>
                    <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={startScan}
                        disabled={!allowScan}
                      >
                        {t('scanAgainRefreshOrientations')}
                      </Button>

                      <Button variant="text" color="inherit" size="small" onClick={onCancel}>
                        {t('cancel')}
                      </Button>
                    </Stack>
                  </Alert>
                ) : (
                  <Alert severity="info">
                    <AlertTitle sx={{ m: 0 }}>{t('scanCompleted')}</AlertTitle>
                    <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                      <Button variant="contained" color="primary" size="small" onClick={startScan}>
                        {t('scanAgainRefreshOrientations')}
                      </Button>
                      <LoadingButton
                        variant="outlined"
                        color="inherit"
                        size="small"
                        loading={isRefreshingOrientation}
                        onClick={handleRefreshOrientation}
                      >
                        {t('refreshOrientation')}
                      </LoadingButton>
                      <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                        {t('cancel')}
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </>
            ) : (
              <Stack justifyContent="center" alignItems="center">
                <Button variant="contained" color="primary" onClick={startScan}>
                  {t('startScan')}
                </Button>
              </Stack>
            )}

            {scannedRuuviTags.length > 0 && (
              <>
                <RuuviTagScannerList
                  ruuviTags={scannedRuuviTags}
                  onSelect={handleSelect}
                  onExitInstallation={onCancel}
                  onTagUpdate={handleTagUpdate}
                />

                {!isScanning && !isScanCompleted && (
                  <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                    <Button variant="contained" color="primary" size="small" onClick={startScan}>
                      {t('scanAgainRefreshOrientations')}
                    </Button>
                    <LoadingButton
                      variant="outlined"
                      color="inherit"
                      size="small"
                      loading={isRefreshingOrientation}
                      onClick={handleRefreshOrientation}
                    >
                      {t('refreshOrientation')}
                    </LoadingButton>
                  </Stack>
                )}
              </>
            )}
          </>
        )}

        {!isScanning && (
          <Box sx={{ mt: 2 }}>
            <RuuviTagMacAddressInput
              disabled={isLoading}
              onChange={handleManualMacChange}
              value={macAddress}
            />

            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
              <RuuviTagQrCodeButton disabled={isLoading} onMacDetected={handleQrMacDetected} />
            </Stack>

            {notFoundMessage && (
              <Alert severity={notFoundAlertSeverity} sx={{ mt: 2 }}>
                <AlertTitle sx={{ m: 0 }}>{notFoundMessage}</AlertTitle>
                {canCreateRuuviTag() && macAddress && (
                  <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2 }}>
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      onClick={handleCancelCreate}
                    >
                      {t('actions:cancel')}
                    </Button>

                    <RuuviTagCreateButton macAddress={macAddress} afterCreate={onChange} />
                  </Stack>
                )}
              </Alert>
            )}
          </Box>
        )}
      </Stack>
    </>
  )
}
